import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Calendar,
  Users,
  User,
  BarChart3,
  Dumbbell,
  DollarSign,
  Building,
  LogOut,
  Sun,
  Moon,
  ChevronDown,
  Shield,
} from "lucide-react";
import { useApp } from "../../contexts/AppContext";
import { useAuth } from "../../App";
import { useTheme } from "../../contexts/ThemeContext";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../ui/LoadingSpinner";

const Sidebar = ({ isCollapsed, toggleSidebar, onNavigate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme, colors } = useTheme();
  const {
    organizations,
    selectedOrganization,
    setSelectedOrganization,
    loading: organizationsLoading,
  } = useApp();

  const getUserRoles = () => {
    try {
      if (user?.session) {
        const idToken = user.session.getIdToken();
        const payload = idToken.payload;
        const groups = payload["cognito:groups"] || [];

        const roles = [];
        if (groups.includes("SuperAdmins")) roles.push("super_admin");
        if (groups.includes("OrganizationManager")) roles.push("organization_manager");
        if (groups.includes("Trainers")) roles.push("trainer");
        if (groups.includes("Students")) roles.push("student");

        // Add any other dynamic groups
        groups.forEach(g => {
          if (!["SuperAdmins", "OrganizationManager", "Trainers", "Students"].includes(g)) {
            roles.push(g);
          }
        });

        return roles.length > 0 ? roles : ["student"];
      }
      return ["student"];
    } catch (error) {
      console.error("Error getting user roles:", error);
      return ["student"];
    }
  };

  const getUserPages = () => {
    try {
      if (user?.pages) {
        return user.pages;
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  const userRoles = getUserRoles();
  const primaryRole = userRoles[0];
  const userPages = getUserPages();

  const getMenuItems = (roles, pages) => {
    const isAdmin = roles.includes("super_admin");

    const allItems = [
      {
        icon: Calendar,
        label: t("sidebar.sessions"),
        path: "/sessions",
        order: 1,
      },
      {
        icon: Users,
        label: t("sidebar.trainers"),
        path: "/trainers",
        order: 2,
      },
      {
        icon: User,
        label: t("sidebar.students"),
        path: "/students",
        order: 3,
      },
      {
        icon: Dumbbell,
        label: t("sidebar.exercises"),
        path: "/exercises",
        order: 4,
      },
      {
        icon: BarChart3,
        label: t("sidebar.analytics"),
        path: "/analytics",
        order: 5,
      },
      {
        icon: DollarSign,
        label: t("sidebar.finance"),
        path: "/finance",
        order: 6,
      },
      {
        icon: Building,
        label: t("sidebar.organizations"),
        path: "/organizations",
        order: 7,
      },
      {
        icon: Shield,
        label: t("sidebar.roles"),
        path: "/roles",
        order: 8,
      },
      {
        icon: Shield,
        label: t("sidebar.permissions"),
        path: "/permissions",
        order: 9,
      }
    ];

    return allItems.filter((item) => {
      if (isAdmin) return true;
      // Check if the exact path or '*' is in the user's pages array
      return pages.includes(item.path) || pages.includes("*");
    }).sort((a, b) => a.order - b.order);
  };

  const menuItems = getMenuItems(userRoles, userPages);

  const handleNavigation = (path) => {
    navigate(path);
    if (onNavigate) {
      onNavigate();
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case "super_admin":
        return "Super Admin";
      case "organization_manager":
        return "Org. Manager";
      case "trainer":
        return "Trainer";
      case "student":
        return "Student";
      default:
        // Capitalize and remove underscores for custom roles
        return role ? role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' ') : role;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "super_admin":
        return "bg-indigo-500/10 text-indigo-400 ring-1 ring-indigo-500/20";
      case "organization_manager":
        return "bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/20";
      case "trainer":
        return "bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/20";
      case "student":
        return "bg-zinc-500/10 text-zinc-400 ring-1 ring-zinc-500/20";
      default:
        return "bg-zinc-500/10 text-zinc-400 ring-1 ring-zinc-500/20";
    }
  };


  const handleSignOut = () => {
    if (logout) {
      logout();
    }
    navigate("/login");
  };

  return (
    <div
      className={`
      sidebar-container
      ${isCollapsed ? "w-20" : "w-72 max-w-[85vw]"}
      bg-white dark:bg-[#09090b] border-gray-200 dark:border-zinc-800
    `}
    >
      {/* Top Section: Logo, Role, Organization */}
      <div className="relative p-6 z-10 border-b border-transparent">
        {/* Logo */}
        <div
          className="flex items-center justify-center mb-8"
          onClick={() => navigate("/sessions")}
        >
          <div
            className={`
            sidebar-logo-container
            ${isCollapsed ? "w-10 h-10" : "w-16 h-16"}
          `}
          >
            {organizations.filter((x) => x.name === selectedOrganization)[0]
              ?.logo ? (
              <img
                src={
                  organizations.filter(
                    (x) => x.name === selectedOrganization,
                  )[0]?.logo
                }
                alt="Logo"
                className="w-full h-full object-contain"
              />
            ) : (
              <img
                src={theme === "dark" ? "/app_logo_dark.png" : "/athleticslogo.png"}
                alt="Logo"
                className="w-full h-full object-contain filter drop-shadow-lg"
              />
            )}
          </div>
        </div>

        {!isCollapsed && (
          <div className="animate-in fade-in slide-in-from-top-4 duration-500">
            {/* User Role Badge */}
            <div className="mb-8 flex justify-center">
              <span
                className={`
                px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.2em] shadow-sm ${getRoleBadgeColor(primaryRole)}
              `}
              >
                {getRoleDisplayName(primaryRole)}
              </span>
            </div>

            {/* Organization Dropdown */}
            {(userRoles.includes("super_admin") ||
              userRoles.includes("organization_manager") ||
              userRoles.includes("trainer") ||
              !userRoles.includes("student")) &&
              (organizationsLoading ? (
                <div className="flex items-center justify-center py-2">
                  <LoadingSpinner size="small" />
                </div>
              ) : organizations.length > 0 && (
                <div className="relative group">
                  <select
                    value={selectedOrganization || ""}
                    onChange={(e) => {
                      setSelectedOrganization(e.target.value);
                      window.location.reload();
                    }}
                    className={`
                      w-full px-5 py-3.5 appearance-none
                      ${colors.inputBg} ${colors.inputBorder}
                      sidebar-org-btn group
                      text-sm font-bold ${colors.inputText}
                    `}
                  >
                    {organizations.length === 0 ? (
                      <option value="" className={colors.textTertiary}>
                        No organizations
                      </option>
                    ) : (
                      organizations.map((org) => (
                        <option
                          key={org.id}
                          value={org.name}
                          className={`${colors.inputText} ${colors.inputBg}`}
                        >
                          {org.name}
                        </option>
                      ))
                    )}
                  </select>
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <ChevronDown className={`w-4 h-4 ${colors.textTertiary}`} />
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto scrollbar-hide z-10 space-y-1">
        {menuItems.map((item) => (
          <button
            key={item.path}
            onClick={() => handleNavigation(item.path)}
            className={`
              sidebar-nav-item group transition-all duration-300
              ${isCollapsed ? "justify-center px-0" : "px-4"}
              ${isActive(item.path)
                ? `${colors.bgActive} border-r-4 border-indigo-500 ${colors.textPrimary} shadow-indigo-500/5`
                : `${colors.textSecondary} ${colors.bgHover} border-r-4 border-transparent`
              }
            `}
            title={isCollapsed ? item.label : undefined}
          >
            <item.icon
              className={`
              w-5 h-5 transition-transform duration-300 group-hover:scale-110
              ${isActive(item.path) ? "text-indigo-400" : colors.textTertiary}
            `}
            />


            {!isCollapsed && (
              <span
                className={`text-sm font-medium ${isActive(item.path)
                  ? colors.textPrimary
                  : colors.textSecondary
                  }`}
              >
                {item.label}
              </span>
            )}
          </button>
        ))}
      </nav>

      {/* Bottom Section: Theme & Sign Out */}
      {/* Bottom Section: Theme & Sign Out */}
      <div
        className={`p-4 mt-auto border-t ${colors.borderSecondary} space-y-2 z-10 bg-transparent`}
      >
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className={`
            w-full flex items-center
            ${!isCollapsed ? "px-3 justify-start" : "justify-center"} py-2.5
            rounded-lg transition-colors duration-200
            ${colors.textSecondary} ${colors.bgHover}
            group
          `}
          title={
            isCollapsed
              ? theme === "dark"
                ? "Light Mode"
                : "Dark Mode"
              : undefined
          }
        >
          {theme === "dark" ? (
            <Sun
              className={`w-5 h-5 group-hover:text-yellow-400 transition-colors ${!isCollapsed && "mr-3"
                }`}
            />
          ) : (
            <Moon
              className={`w-5 h-5 group-hover:text-indigo-400 transition-colors ${!isCollapsed && "mr-3"
                }`}
            />
          )}
          {!isCollapsed && (
            <span className="text-sm font-medium">
              {theme === "dark" ? "Light Mode" : "Dark Mode"}
            </span>
          )}
        </button>

        {/* Sign Out */}
        <button
          onClick={handleSignOut}
          className={`
            w-full flex items-center
            ${!isCollapsed ? "px-3 justify-start" : "justify-center"} py-2.5
            rounded-lg transition-colors duration-200
            text-red-500 hover:bg-red-500/10
            group
          `}
          title={isCollapsed ? t("sidebar.signOut") : undefined}
        >
          <LogOut className={`w-5 h-5 ${!isCollapsed ? "mr-3" : ""}`} />
          {!isCollapsed && (
            <span className="text-sm font-medium">{t("sidebar.signOut")}</span>
          )}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
